const DEFAULT_PATH = '/inbox'

// Code points below `!`: the C0 controls and the space. A negated range, because a literal
// control character in a regex reads as a mistake.
const LEADING_C0_OR_SPACE = /^[^\u0021-\u{10FFFF}]+/u

/**
 * Constrains a redirect target to a path inside this app.
 *
 * The post-login `?next=` parameter is attacker-supplied — anyone can send a
 * provider a link to the CRM carrying one — and it is handed to the router once
 * Auth0 returns. An unconstrained value navigates a *freshly authenticated* user
 * off-site, which is the highest-value position for a phishing hand-off.
 *
 * Browsers resolve a leading `//`, `\\`, `/\` or `\/` as protocol-relative, so all
 * four escape the origin. React Router only rejects the `//` spelling before
 * v7.18 (CVE-2026-53669), so the backslash forms reach the DOM as written; this
 * check does not depend on the router's own normalization either way.
 */
export const toSafeInternalPath = (next: string | null | undefined): string => {
  if (!next) return DEFAULT_PATH

  // Mirror what the URL parser itself discards before resolving, so the prefix
  // checks below see the same string the browser will: tab/LF/CR are dropped
  // wherever they appear, and leading controls and spaces are trimmed. Interior
  // spaces are left alone — the parser encodes those rather than removing them,
  // and stripping them here would corrupt a legitimate path.
  const candidate = next.replace(/[\t\n\r]/g, '').replace(LEADING_C0_OR_SPACE, '')

  // A rooted path cannot carry a scheme, which leaves only the protocol-relative
  // spellings to exclude.
  if (!candidate.startsWith('/')) return DEFAULT_PATH
  if (/^[/\\]{2}/.test(candidate)) return DEFAULT_PATH

  return candidate
}

/**
 * `decodeURI` throws on a malformed escape sequence (`%E0%A4%A`), which an
 * attacker controls just as easily as the path itself.
 */
export const decodeSafeInternalPath = (rawNext: string | null | undefined): string => {
  if (!rawNext) return DEFAULT_PATH
  try {
    return toSafeInternalPath(decodeURI(rawNext))
  } catch {
    return DEFAULT_PATH
  }
}
